<script lang="ts">
import { type MediaFile, MediaUtils } from '@monogrid/vue-boost/cms'
import { templateRef, useMediaControls } from '@vueuse/core'
import { computed, defineComponent, onMounted, PropType, ref, watch } from 'vue'

import CtaSecondary from '@/components/atoms/CtaSecondary.vue'
import VideoPlayerProgress from '@/components/atoms/VideoPlayerProgress.vue'

export default defineComponent({
  name: 'VideoPlayer',
  components: {
    CtaSecondary,
    VideoPlayerProgress
  },
  props: {
    autoplay: { type: Boolean, default: false },
    data: { type: Object as PropType<MediaFile>, required: true },
    loop: { type: Boolean, default: false },
    muted: { type: Boolean, default: false }
  },
  emits: ['ended', 'pause', 'play', 'ready'],
  setup (props, { expose, emit }) {
    const video = templateRef<HTMLVideoElement>('video')
    const { playing, muted: videoMuted, currentTime, duration } = useMediaControls(video)
    const progress = computed(() => currentTime.value / duration.value)

    const showUi = ref(true)

    const videoConfig = computed(() => {
      const metadata = MediaUtils.getMetaData(props.data)
      return {
        src: `/media/${props.data.id}?t=0.1`,
        width: Number(metadata['video-width']),
        height: Number(metadata['video-height'])
      }
    })

    watch(() => props.muted, value => (videoMuted.value = value), { immediate: true })

    expose({
      video,
      play: () => (playing.value = true),
      pause: () => (playing.value = false),
      toggleMuted: () => (videoMuted.value = !videoMuted.value)
    })

    onMounted(() => video.value.load())

    return {
      videoMuted,
      playing,
      progress,
      showUi,
      videoConfig,
      duration,
      currentTime
    }
  }
})
</script>

<template>
  <div class="relative">
    <video
      v-if="videoConfig"
      ref="video"
      class="w-full h-full"
      v-bind="videoConfig"
      crossorigin="anonymous"
      playsinline="true"
      preload="auto"
      muted
      disablePictureInPicture
      :autoplay="autoplay"
      :loop="loop"
      @click="playing = !playing"
    />
    <Transition>
      <div v-if="showUi" class="absolute bottom-4 left-4 right-4 z-2">
        <div class="controls mx-auto">
          <div class="flex justify-between mb-2">
            <CtaSecondary class="uppercase" @click="playing = !playing">{{ playing ? $t('Pause') : 'Play' }}</CtaSecondary>
            <CtaSecondary class="uppercase" @click="videoMuted = !videoMuted">{{ $t('Sound') }} {{ videoMuted ? 'ON' : 'OFF' }}</CtaSecondary>
          </div>
          <VideoPlayerProgress v-model="currentTime" :max="duration" :progress="progress" />
        </div>
      </div>
    </Transition>
  </div>
</template>
<style lang="scss" scoped>
.controls {
  width: 90%;
  max-width: rem(1024);
}
</style>
